import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
  Divider,
  Grid,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Snackbar,
  Alert,
  Autocomplete,
  Checkbox,
  OutlinedInput
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import {
  Add as AddIcon,
  AssignmentOutlined as AssignmentIcon,
  PersonOutline as PersonIcon,
  GroupOutlined as GroupIcon,
  CalendarToday as DateIcon,
  PriorityHigh as PriorityIcon,
  LabelOutlined as LabelIcon,
  BusinessOutlined as BusinessIcon,
  NoteOutlined as NoteIcon,
  FormatListBulleted as ListIcon,
  UploadFile as UploadIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline as CheckCircleIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { base_url, getToken } from '../../config/config';
import ANRModal from '../Aufgabe/KanbanComponents/ANRModal';
import InfoIcon from '@mui/icons-material/Info';

// Default colors for fallback
const defaultColors = {
  primary: {
    100: '#d0d1d5',
    200: '#a1a4ab',
    300: '#727681',
    400: '#1F2A40',
    500: '#141b2d',
  },
  grey: {
    100: '#e0e0e0',
    200: '#c2c2c2',
    300: '#a3a3a3',
    400: '#858585',
    500: '#666666',
    600: '#4d4d4d',
    700: '#333333',
    800: '#1a1a1a',
    900: '#0a0a0a',
  },
  greenAccent: {
    100: '#dbf5ee',
    200: '#b7ebde',
    300: '#94e2cd',
    400: '#70d8bd',
    500: '#4cceac',
    600: '#3da58a',
    700: '#2e7c67',
    800: '#1e5245',
    900: '#0f2922',
  },
  redAccent: {
    100: '#f8dcdb',
    200: '#f1b9b7',
    300: '#e99592',
    400: '#e2726e',
    500: '#db4f4a',
    600: '#af3f3b',
    700: '#832f2c',
    800: '#58201e',
    900: '#2c100f',
  },
  blueAccent: {
    100: '#e1e2fe',
    200: '#c3c6fd',
    300: '#a4a9fc',
    400: '#868dfb',
    500: '#6870fa',
    600: '#535ac8',
    700: '#3e4396',
    800: '#2a2d64',
    900: '#151632',
  },
  orange: {
    400: '#FB8C00',
    500: '#EF6C00',
  }
};

// Text templates for quick insert
const TEXT_TEMPLATES = [
  "Material entspricht nicht den Spezifikationen.",
  "Lieferung erfolgte nicht termingerecht.",
  "Qualitätsmängel bei folgenden Komponenten festgestellt:",
  "Produkt weist Beschädigungen durch Transport auf.",
  "Dokumentation ist unvollständig oder fehlerhaft.",
  "Fehlerhafte Abmessungen bei geliefertem Material.",
  "Fehler bei der Auftragserfassung im System.",
  "Produktion verwendet veraltete Dokumentation.",
  "Prozessschritte wurden nicht eingehalten.",
  "Qualitätskontrolle hat fehlerhafte Teile nicht erkannt.",
];

// Priorities
const PRIORITIES = [
  { value: 'low', label: 'Niedrig', color: '#2ecc71' },
  { value: 'medium', label: 'Mittel', color: '#f39c12' },
  { value: 'high', label: 'Hoch', color: '#e74c3c' },
];

// Status options
const STATUS_OPTIONS = [
  { value: 'open', label: 'Offen', color: '#e74c3c' },
  { value: 'in-progress', label: 'In Bearbeitung', color: '#3498db' },
  { value: 'completed', label: 'Abgeschlossen', color: '#2ecc71' },
];

const ReklamationForm = ({ANR = '' }) => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const theme = useTheme();
  let colors;
  try {
    colors = tokens(theme.palette.mode);
  } catch (error) {
    console.warn("Theme error, using fallback colors", error);
    colors = defaultColors;
  }
  const navigate = useNavigate();
  const token = getToken();
  const isDarkMode = theme.palette.mode === 'dark';

  // User info
  const userState = useSelector((state) => state.user);
  
  // Handle case where user._id might be an array or string
  let loggedInUserId;
  if (userState?.user?._id) {
    if (Array.isArray(userState.user._id)) {
      // Extract the ID from the array if it's in the format ['Timur Serbay', '67df49c58415952429c170c5']
      loggedInUserId = userState.user._id[1] || '';
      console.log('User ID is an array, using second element:', loggedInUserId);
    } else {
      loggedInUserId = userState.user._id;
    }
  } else {
    loggedInUserId = '';
  }
  
  const loggedInUserType = userState?.user?.userType || 'user';
  const username = `${userState?.user?.firstName || ''} ${userState?.user?.lastName || ''}`.trim();

  // Form steps
  const [activeStep, setActiveStep] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    creator: username,
    date: new Date().toISOString().split('T')[0],
    recipientType: 'person',
    recipientName: '',
    processType: 'internal',
    sendEmail: false,
    priority: 'medium',
    status: 'open',
    projectId: '',
    anr: ANR,
    auftragsbezeichnung: '',
    referenceOrderNumber: '',
    referenceText: '',
    description: '',
    processImprovements: [],
    assignedTeam: '',
    deadline: '',
    resolutionDetails: '',
    improvementMeasures: '',
    initialComment: '',
    kundeId: null,
    visibleToEmployees: [], // New field for employee visibility
    signatureRequired: false, // New field to require signatures
  });

  // File upload
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Form lock state (for edit mode when completed)
  const [isLocked, setIsLocked] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [kundeLoading, setKundeLoading] = useState(false);

  // Data states
  const [users, setUsers] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [Kunden, setKunden] = useState([]);
  const [selectedKunde, setselectedKunde] = useState(null);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Modal states
  const [anrModalOpen, setAnrModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchUsers();
    fetchProcesses();
    fetchKunde();
    fetchAvailableEmployees();

    if (isEditMode) {
      fetchReklamationDetails();
    }
  }, [isEditMode, id]);

  // Fetch reklamation details if in edit mode
  const fetchReklamationDetails = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getReklamationById/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const reklamation = response.data;

      // Map the data from API to form
      setFormData({
        creator: reklamation.creator?.firstName
          ? `${reklamation.creator.firstName} ${reklamation.creator.lastName}`
          : reklamation.creator?.name || username,
        date: new Date(reklamation.date).toISOString().split('T')[0],
        recipientType: reklamation.recipientType || 'person',
        recipientName: reklamation.recipient?.firstName
          ? `${reklamation.recipient.firstName} ${reklamation.recipient.lastName}`
          : reklamation.recipient?.name || '',
        processType: reklamation.processType || 'internal',
        sendEmail: reklamation.emailSent || false,
        priority: reklamation.priority || 'medium',
        status: reklamation.status || 'open',
        projectId: reklamation.project?._id || '',
        anr: reklamation.anr || '',
        auftragsbezeichnung: reklamation.auftragsbezeichnung || '',
        referenceOrderNumber: reklamation.referenceOrderNumber || '',
        referenceText: reklamation.referenceText || '',
        description: reklamation.description || '',
        processImprovements: reklamation.processImprovements || [],
        assignedTeam: reklamation.assignedTeam || '',
        deadline: reklamation.deadline
          ? new Date(reklamation.deadline).toISOString().split('T')[0]
          : '',
        resolutionDetails: reklamation.resolutionDetails || '',
        improvementMeasures: reklamation.improvementMeasures || '',
        initialComment: '',
        kundeId : reklamation.kunde?._id || null,
      });
      setselectedKunde(reklamation.kunde ?? null);

      // Set uploaded files
      if (reklamation.documents && reklamation.documents.length > 0) {
        setUploadedFiles(reklamation.documents);
      }

      // Lock form if completed
      if (reklamation.status === 'completed') {
        setIsLocked(true);
      }
    } catch (error) {
      console.error('Error fetching reklamation details:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Reklamationsdaten',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch users (both admins and employees)
  const fetchUsers = async () => {
    try {
      // Get admins
      const adminRes = await axios.get(`${base_url}/v1/getAllAdmins`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Get employees
      const empRes = await axios.get(`${base_url}/v1/getAllEmployees`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Map to unified format
      const adminList = adminRes.data.users || [];
      const employeeList = empRes.data || [];

      const admins = adminList.map((adm) => ({
        _id: adm._id,
        displayName: `${adm.firstName} ${adm.lastName}`,
        userModel: 'Admin',
      }));

      const employees = employeeList.map((emp) => ({
        _id: emp._id,
        displayName: emp.name,
        userModel: 'Employee',
      }));

      // Combine
      setUsers([...admins, ...employees]);
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Benutzer',
        severity: 'error',
      });
    }
  };

  // Fetch processes
  const fetchProcesses = async () => {
    setProcessesLoading(true);
    try {
      const response = await axios.get(`${base_url}/v1/getProcesses`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Assuming the API returns an array of processes
      if (response.data && Array.isArray(response.data)) {
        setProcesses(response.data);
      } else {
        // If API doesn't exist yet, use mock data
        setProcesses([
          { id: 'PI-001', title: 'Standardisierung der Qualitätsprüfung', link: '/processes/PI-001' },
          { id: 'PI-002', title: 'Verbesserte Kommunikation mit Kunden', link: '/processes/PI-002' },
          { id: 'PI-003', title: 'Optimierung der Materialbestellung', link: '/processes/PI-003' },
          { id: 'PI-004', title: 'Automatisierung des Dokumentationsprozesses', link: '/processes/PI-004' },
          { id: 'PI-005', title: 'Verbesserung der Fehleranalyse', link: '/processes/PI-005' },
          { id: 'PI-006', title: 'Schulungsprogramm für Qualitätssicherung', link: '/processes/PI-006' },
        ]);
      }
    } catch (error) {
      console.error('Error fetching processes:', error);
      // If API doesn't exist yet, use mock data
      setProcesses([
        { id: 'PI-001', title: 'Standardisierung der Qualitätsprüfung', link: '/processes/PI-001' },
        { id: 'PI-002', title: 'Verbesserte Kommunikation mit Kunden', link: '/processes/PI-002' },
        { id: 'PI-003', title: 'Optimierung der Materialbestellung', link: '/processes/PI-003' },
        { id: 'PI-004', title: 'Automatisierung des Dokumentationsprozesses', link: '/processes/PI-004' },
        { id: 'PI-005', title: 'Verbesserung der Fehleranalyse', link: '/processes/PI-005' },
        { id: 'PI-006', title: 'Schulungsprogramm für Qualitätssicherung', link: '/processes/PI-006' },
      ]);
    } finally {
      setProcessesLoading(false);
    }
  };

  // Fetch Kunde
  const fetchKunde = async () => {
    try {
      setKundeLoading(true);
      const response = await axios.get(`${base_url}/v1/getKunden`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response && response.data) {
        setKunden(response.data.data);
      } else {
        setKunden([]);
      }
      setKundeLoading(false);
    } catch (err) {
      console.error('Error fetching processes:', err);
      setKundeLoading(false);
    }
  };

  // Fetch available employees
  const fetchAvailableEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await axios.get(
        `${base_url}/v1/getAllEmployees`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setAvailableEmployees(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setAvailableEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Mitarbeiter',
        severity: 'error'
      });
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handle employee selection change
  const handleEmployeeSelectionChange = (event) => {
    if (isLocked) return;
    
    const { value } = event.target;
    setFormData({
      ...formData,
      visibleToEmployees: typeof value === 'string' ? value.split(',') : value,
    });
  };

  // Handle form input changes
  const handleChange = (e) => {
    if (isLocked) return;

    const { name, value, checked, type } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle date change
  const handleDateChange = (date, fieldName) => {
    if (isLocked) return;

    if (date) {
      setFormData((prevData) => ({
        ...prevData,
        [fieldName]: date.toISOString().split('T')[0],
      }));
    }
  };

  const handleKundeSelect = (event, value) => {
    if (value) {
      setselectedKunde(value);
      setFormData((prevData) => ({
        ...prevData,
        "kundeId" : value._id,
      }));
    } else {
      setselectedKunde(null);
      setFormData((prevData) => ({
        ...prevData,
        "kundeId" : null,
      }));
    }
  };

  // Handle template selection
  const handleTemplateInsert = (template) => {
    if (isLocked) return;

    setFormData((prevData) => ({
      ...prevData,
      description: prevData.description
        ? `${prevData.description}\n${template}`
        : template,
    }));
  };

  // Handle process improvement selection/deselection
  const handleProcessToggle = (processId) => {
    if (isLocked) return;

    setFormData((prevData) => {
      const currentProcesses = [...prevData.processImprovements];
      const processIndex = currentProcesses.indexOf(processId);

      if (processIndex === -1) {
        currentProcesses.push(processId);
      } else {
        currentProcesses.splice(processIndex, 1);
      }

      return {
        ...prevData,
        processImprovements: currentProcesses,
      };
    });
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    if (isLocked) return;

    const files = Array.from(e.target.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  // Handle file removal
  const handleFileRemove = (index) => {
    if (isLocked) return;

    setSelectedFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  // Handle uploaded file removal
  const handleUploadedFileRemove = (index) => {
    if (isLocked) return;

    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  // Handle project selection from ANR modal
  const handleAnrSelect = (projectData) => {
    setFormData((prev) => ({
      ...prev,
      projectId: projectData.projectId,
      anr: projectData.anr,
      auftragsbezeichnung: projectData.auftragsbezeichnung || '',
    }));
  };

  // Stepper handlers
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (isLocked) return;

    setSubmitting(true);
    try {
      // Prepare form data for submission
      const formDataToSubmit = new FormData();

      // Add filtered form fields (exclude fields that will be handled separately)
      Object.entries(formData).forEach(([key, value]) => {
        // Skip fields that we'll handle separately to avoid validation errors
        if (key === 'creator' || key === 'creatorModel' || key === 'recipient' || key === 'recipientModel') {
          return;
        }
        
        if (key === 'processImprovements' && Array.isArray(value)) {
          formDataToSubmit.append(key, JSON.stringify(value));
        } else if (value !== null && value !== undefined) {
          formDataToSubmit.append(key, value);
        }
      });

      // Handle recipient - ensure recipientName is always treated as an array
      const recipientNames = Array.isArray(formData.recipientName) 
        ? formData.recipientName 
        : formData.recipientName 
        ? [formData.recipientName] 
        : [];
        
      const selectedUser = recipientNames.map((name) => {
        return users.find((user) => user.displayName === name)
      }).filter(user => user !== undefined);
      
      if (selectedUser.length > 0) {
        formDataToSubmit.append('recipient', Array.from(selectedUser.map(user => user._id)));
        formDataToSubmit.append('recipientModel', selectedUser[0].userModel);
      } else {
        formDataToSubmit.append('recipient', loggedInUserId);
        formDataToSubmit.append('recipientModel', loggedInUserType === 'employee' ? 'Employee' : 'Admin');
      }

      // Handle creator - the array case is already handled in the user info extraction
      if (loggedInUserId) {
        formDataToSubmit.append('creator', loggedInUserId);
        formDataToSubmit.append('creatorModel', loggedInUserType === 'employee' ? 'Employee' : 'Admin');
      } else {
        console.warn('No valid creator ID found, the form submission may fail');
      }

      // Add files
      selectedFiles.forEach((file) => {
        formDataToSubmit.append('documents', file);
      });

      let response;
      if (isEditMode) {
        // Update existing reklamation
        response = await axios.put(
          `${base_url}/v1/updateReklamation/${id}`,
          formDataToSubmit,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        setSnackbar({
          open: true,
          message: 'Reklamation erfolgreich aktualisiert',
          severity: 'success',
        });
      } else {
        // Create new reklamation
        response = await axios.post(
          `${base_url}/v1/createReklamation`,
          formDataToSubmit,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        setSnackbar({
          open: true,
          message: 'Reklamation erfolgreich erstellt',
          severity: 'success',
        });
      }

      // Set a timeout to redirect after showing success message
      setTimeout(() => {
        navigate(`/reklamation/${response.data._id}`);
      }, 1500);
    } catch (error) {
      console.error('Error submitting reklamation:', error);
      setSnackbar({
        open: true,
        message: `Fehler beim ${isEditMode ? 'Aktualisieren' : 'Erstellen'} der Reklamation`,
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (isFormModified()) {
      setConfirmDialogOpen(true);
    } else {
      navigate('/reklamation');
    }
  };

  // Check if form is modified
  const isFormModified = () => {
    // For simplicity, we'll just check if any required fields are filled
    return (
      formData.description.trim() !== '' ||
      formData.referenceText.trim() !== '' ||
      formData.anr.trim() !== '' ||
      selectedFiles.length > 0 ||
      formData.processImprovements.length > 0
    );
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusObj = STATUS_OPTIONS.find((s) => s.value === status);
    return statusObj ? statusObj.color : '#e74c3c';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const priorityObj = PRIORITIES.find((p) => p.value === priority);
    return priorityObj ? priorityObj.color : '#e74c3c';
  };

  // Stepper steps
  const steps = [
    {
      label: 'Grundinformationen',
      description: 'Allgemeine Informationen zur Reklamation',
      content: (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* PROCESS TYPE SELECTION */}
          <Grid item xs={12}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                bgcolor: formData.processType === 'internal' 
                  ? alpha(colors.greenAccent[500], 0.15)
                  : alpha(colors.blueAccent[500], 0.15),
                border: `1px solid ${
                  formData.processType === 'internal'
                    ? colors.greenAccent[400]
                    : colors.blueAccent[400]
                }`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Prozesstyp
              </Typography>
              <RadioGroup
                row
                name="processType"
                value={formData.processType}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="internal"
                  control={<Radio disabled={isLocked} />}
                  label="Interner Prozess"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontWeight: formData.processType === 'internal' ? 'bold' : 'normal',
                      color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[500],
                    },
                  }}
                />
                <Tooltip title="Interne Prozessverbesserungen und Qualitätssicherung">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <FormControlLabel
                  value="external"
                  control={<Radio disabled={isLocked} />}
                  label="Externer Prozess"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontWeight: formData.processType === 'external' ? 'bold' : 'normal',
                      color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[500],
                    },
                  }}
                />
                <Tooltip title="Reklamationen von/an externe Lieferanten oder Kunden">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </RadioGroup>
            </Paper>
          </Grid>

          {/* CREATION INFO */}
          {/* Kunde (Customer) Selection - shown when process type is external */}
          {formData.processType === 'external' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Kundenauswahl
              </Typography>
              {kundeLoading ? (
                <CircularProgress />
              ) : (
                <Autocomplete
                  options={Kunden}
                  getOptionLabel={(option) =>
                    `${option.bezeichnung} (${option.konto})`
                  }
                  onChange={handleKundeSelect}
                  value={selectedKunde}
                  renderInput={(params) => (
                    <TextField {...params} label='Kunde' variant='outlined' />
                  )}
                  freeSolo
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase();
                    return options.filter(
                      (option) =>
                        option.konto.includes(inputValue) ||
                        option.bezeichnung.toLowerCase().includes(inputValue) ||
                        option.kurzbezeichnung.toLowerCase().includes(inputValue),
                    );
                  }}
                />
              )}
            </Grid>
          )}
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Ersteller"
              name="creator"
              value={formData.creator}
              onChange={handleChange}
              disabled={true}
              InputProps={{
                startAdornment: <PersonIcon sx={{ mr: 1, opacity: 0.7 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <DatePicker
                label="Datum"
                value={formData.date ? new Date(formData.date) : null}
                onChange={(date) => handleDateChange(date, 'date')}
                disabled={isLocked}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      startAdornment: <DateIcon sx={{ mr: 1, opacity: 0.7 }} />,
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="priority-label">Priorität</InputLabel>
              <Select
                labelId="priority-label"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                disabled={isLocked}
                label="Priorität"
                startAdornment={<PriorityIcon sx={{ mr: 1, opacity: 0.7 }} />}
              >
                {PRIORITIES.map((priority) => (
                  <MenuItem key={priority.value} value={priority.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: priority.color,
                          mr: 1,
                        }}
                      />
                      {priority.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* STATUS AND PROJECT REFERENCE */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isLocked}
                label="Status"
                startAdornment={<LabelIcon sx={{ mr: 1, opacity: 0.7 }} />}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: status.color,
                          mr: 1,
                        }}
                      />
                      {status.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper
              variant="outlined"
              onClick={() => !isLocked && setAnrModalOpen(true)}
              sx={{
                p: 2,
                cursor: isLocked ? 'default' : 'pointer',
                borderColor: formData.anr ? colors.greenAccent[500] : colors.grey[500],
                borderWidth: formData.anr ? 2 : 1,
                borderStyle: 'dashed',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                '&:hover': {
                  borderColor: !isLocked
                    ? colors.primary[400]
                    : formData.anr
                    ? colors.greenAccent[500]
                    : colors.grey[500],
                  bgcolor: !isLocked ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                },
              }}
            >
              {formData.anr ? (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <BusinessIcon sx={{ mr: 2, color: colors.primary[500] }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {formData.anr}
                    </Typography>
                    <Typography variant="body2" color={colors.grey[400]}>
                      {formData.auftragsbezeichnung || 'Keine Bezeichnung'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <AddIcon sx={{ mr: 2, color: colors.grey[500] }} />
                  <Typography color={colors.grey[500]}>
                    Projekt auswählen (ANR)
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* REFERENCE INFO */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Referenz Auftragsnummer"
              name="referenceOrderNumber"
              value={formData.referenceOrderNumber}
              onChange={handleChange}
              disabled={isLocked}
              InputProps={{
                startAdornment: <AssignmentIcon sx={{ mr: 1, opacity: 0.7 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Referenz Text"
              name="referenceText"
              value={formData.referenceText}
              onChange={handleChange}
              disabled={isLocked}
              InputProps={{
                startAdornment: <NoteIcon sx={{ mr: 1, opacity: 0.7 }} />,
              }}
            />
          </Grid>

          {/* DEADLINE */}
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <DatePicker
                label="Fälligkeitsdatum"
                value={formData.deadline ? new Date(formData.deadline) : null}
                onChange={(date) => handleDateChange(date, 'deadline')}
                disabled={isLocked}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      startAdornment: <DateIcon sx={{ mr: 1, opacity: 0.7 }} />,
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Zuständiges Team"
              name="assignedTeam"
              value={formData.assignedTeam}
              onChange={handleChange}
              disabled={isLocked}
              InputProps={{
                startAdornment: <GroupIcon sx={{ mr: 1, opacity: 0.7 }} />,
              }}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Verantwortlichkeiten',
      description: 'Empfänger und Benachrichtigungseinstellungen',
      content: (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Empfängertyp</FormLabel>
              <RadioGroup
                row
                name="recipientType"
                value={formData.recipientType}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="person"
                  control={<Radio disabled={isLocked} />}
                  label="Person"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[900],
                    },
                  }}
                />
                <FormControlLabel
                  value="group"
                  control={<Radio disabled={isLocked} />}
                  label="Gruppe"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[900],
                    },
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Empfängername</InputLabel>
              <Select
                name="recipientName"
                multiple
                value={Array.isArray(formData.recipientName) ? formData.recipientName : formData.recipientName ? [formData.recipientName] : []}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    recipientName: value,
                  }));
                }}
                disabled={isLocked}
                label="Empfängername"
                renderValue={(selected) =>
                  Array.isArray(selected)
                    ? selected.map((name) => {
                        const user = users.find((u) => u.displayName === name);
                        return user ? `${user.displayName} (${user.userModel})` : name;
                      }).join(', ')
                    : ''
                }
              >
                <MenuItem value="">
                  <em>Auswählen</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user._id} value={user.displayName}>
                    <Checkbox
                      checked={
                        Array.isArray(formData.recipientName)
                          ? formData.recipientName.indexOf(user.displayName) > -1
                          : formData.recipientName === user.displayName
                      }
                    />
                    <ListItemText primary={`${user.displayName} (${user.userModel})`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="sendEmail"
                  checked={formData.sendEmail}
                  onChange={handleChange}
                  disabled={isLocked}
                />
              }
              label="Information per E-Mail senden"
            />
          </Grid>

          {/* Employee Visibility Section */}
          <Grid item xs={12}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                bgcolor: colors.primary[400],
                borderColor: colors.greenAccent[400],
                borderRadius: 2
              }}
            >
              <Typography variant="h6" color={colors.greenAccent[400]} gutterBottom>
                Mitarbeiter-Sichtbarkeit & Unterschriften
              </Typography>
              <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2 }}>
                Wählen Sie die Mitarbeiter aus, die diese Reklamation sehen können
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Sichtbar für Mitarbeiter</InputLabel>
                <Select
                  multiple
                  value={formData.visibleToEmployees}
                  onChange={handleEmployeeSelectionChange}
                  input={<OutlinedInput label="Sichtbar für Mitarbeiter" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((employeeId) => {
                        const employee = availableEmployees.find(emp => emp._id === employeeId);
                        return (
                          <Chip 
                            key={employeeId} 
                            label={employee ? `${employee.name || employee.fullName || employee.email}` : employeeId}
                            size="small" 
                          />
                        );
                      })}
                    </Box>
                  )}
                  disabled={isLocked || loadingEmployees}
                >
                  <MenuItem value="">
                    <em>Alle Mitarbeiter (Standardeinstellung)</em>
                  </MenuItem>
                  {availableEmployees.map((employee) => (
                    <MenuItem key={employee._id} value={employee._id}>
                      <Checkbox checked={formData.visibleToEmployees.indexOf(employee._id) > -1} />
                      <ListItemText 
                        primary={employee.name || employee.fullName || employee.email} 
                        secondary={employee.email || employee.position}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {loadingEmployees && (
                  <Typography variant="caption" color={colors.grey[400]} sx={{ mt: 1 }}>
                    Mitarbeiter werden geladen...
                  </Typography>
                )}
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.signatureRequired}
                    onChange={handleChange}
                    name="signatureRequired"
                    color="primary"
                    disabled={isLocked}
                  />
                }
                label={
                  <Typography color={colors.grey[100]}>
                    Unterschriften von ausgewählten Mitarbeitern erforderlich
                  </Typography>
                }
              />
              
              {formData.signatureRequired && formData.visibleToEmployees.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Die folgenden Mitarbeiter müssen diese Reklamation bestätigen:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.visibleToEmployees.map((employeeId) => {
                      const employee = availableEmployees.find(emp => emp._id === employeeId);
                      return (
                        <Chip 
                          key={employeeId}
                          label={employee ? `${employee.name || employee.fullName || employee.email}` : employeeId}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Initiale Notiz/Kommentar"
              name="initialComment"
              value={formData.initialComment}
              onChange={handleChange}
              disabled={isLocked || isEditMode}
              placeholder="Fügen Sie einen initialen Kommentar hinzu (dieser wird als erster Kommentar in der Kommunikationshistorie angezeigt)"
              InputProps={{
                startAdornment: (
                  <NoteIcon
                    sx={{ mr: 1, mt: 1, verticalAlign: 'top', opacity: 0.7 }}
                  />
                ),
              }}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Beschreibung & Dokumente',
      description: 'Detaillierte Problembeschreibung und Anhänge',
      content: (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Detaillierte Beschreibung"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={6}
              required
              disabled={isLocked}
              placeholder="Beschreiben Sie das Problem, den Vorfall oder die Reklamation im Detail..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: colors.grey[500],
                  },
                  '&:hover fieldset': {
                    borderColor: colors.primary[400],
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary[400],
                  },
                },
              }}
            />
          </Grid>

          {!isLocked && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Schnell-Einfüge Textvorlagen:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {TEXT_TEMPLATES.map((template, index) => (
                  <Chip
                    key={index}
                    label={
                      template.length > 30
                        ? template.substring(0, 30) + '...'
                        : template
                    }
                    onClick={() => handleTemplateInsert(template)}
                    color="primary"
                    variant="outlined"
                    sx={{ my: 0.5 }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Dokumente und Anhänge
            </Typography>

            {!isLocked && (
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
              >
                Dateien hochladen
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileSelect}
                  disabled={isLocked}
                />
              </Button>
            )}

            {selectedFiles.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Ausgewählte Dateien:
                </Typography>
                <List dense>
                  {selectedFiles.map((file, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        !isLocked && (
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleFileRemove(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )
                      }
                    >
                      <ListItemIcon>
                        <AttachFileIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(2)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {uploadedFiles.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Bereits hochgeladene Dateien:
                </Typography>
                <List dense>
                  {uploadedFiles.map((file, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        !isLocked && (
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleUploadedFileRemove(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )
                      }
                    >
                      <ListItemIcon>
                        <AttachFileIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.originalName || `Datei ${index + 1}`}
                        secondary={file.mimeType}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {selectedFiles.length === 0 && uploadedFiles.length === 0 && (
              <Typography variant="body2" color={colors.grey[500]} sx={{ mb: 2 }}>
                Keine Dateien ausgewählt oder hochgeladen
              </Typography>
            )}
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Maßnahmen & Verbesserungen',
      description:
        'Verbesserungsmaßnahmen und Prozessoptimierungen',
      content: (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Prozessverbesserungen
              </Typography>
              <Typography variant="body2" color={colors.grey[400]} sx={{ mb: 2 }}>
                Wählen Sie Prozessverbesserungen aus, die mit dieser Reklamation zusammenhängen.
                Dies hilft bei der Identifizierung von Schwachstellen und der kontinuierlichen Verbesserung.
              </Typography>

              {processesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <List
                  sx={{
                    bgcolor: colors.primary[400],
                    borderRadius: 2,
                    maxHeight: 300,
                    overflow: 'auto',
                    opacity: isLocked ? 0.8 : 1,
                  }}
                >
                  {processes.map((process) => (
                    <ListItem
                      key={process.id}
                      secondaryAction={
                        !isLocked && (
                          <Chip
                            label={
                              formData.processImprovements.includes(process.id)
                                ? 'Ausgewählt'
                                : 'Auswählen'
                            }
                            color={
                              formData.processImprovements.includes(process.id)
                                ? 'primary'
                                : 'default'
                            }
                            onClick={() => handleProcessToggle(process.id)}
                            variant={
                              formData.processImprovements.includes(process.id)
                                ? 'filled'
                                : 'outlined'
                            }
                          />
                        )
                      }
                      sx={{
                        bgcolor: formData.processImprovements.includes(process.id)
                          ? colors.primary[300]
                          : 'transparent',
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: isLocked ? 'transparent' : colors.primary[300],
                        },
                      }}
                    >
                      <ListItemIcon>
                        <ListIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={process.id}
                        secondary={process.title}
                        primaryTypographyProps={{
                          fontWeight: 'bold',
                        }}
                      />
                      {isLocked && formData.processImprovements.includes(process.id) && (
                        <Chip label="Ausgewählt" color="primary" size="small" />
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 2,
                bgcolor: isDarkMode ? 'rgba(30, 40, 50, 0.6)' : 'rgba(245, 245, 245, 0.8)',
                borderRadius: 1,
                borderColor: colors.greenAccent[400]
              }}
              className="shadow-sm"
            >
              <Typography variant="h6" color={colors.greenAccent[400]} gutterBottom>
                Maßnahmen zur Problemlösung
              </Typography>
              <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2 }}>
                Dokumentieren Sie sowohl kurzfristige als auch langfristige Maßnahmen zur Behebung des Problems
              </Typography>
              <TextField
                fullWidth
                label="Kurzfristige Maßnahmen"
                name="resolutionDetails"
                value={formData.resolutionDetails}
                onChange={handleChange}
                multiline
                rows={3}
                disabled={isLocked}
                placeholder="Sofortige Maßnahmen zur Behebung des Problems..."
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? 'rgba(40, 50, 60, 0.5)' : 'white',
                  }
                }}
                className="focus:border-green-400"
              />
              <TextField
                fullWidth
                label="Langfristige Verbesserungen"
                name="improvementMeasures"
                value={formData.improvementMeasures || ''}
                onChange={handleChange}
                multiline
                rows={3}
                disabled={isLocked}
                placeholder="Langfristige Verbesserungen zur Vermeidung ähnlicher Probleme in der Zukunft..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? 'rgba(40, 50, 60, 0.5)' : 'white',
                  }
                }}
                className="focus:border-blue-400"
              />
            </Paper>
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Überprüfung & Abschluss',
      description: 'Übersicht und finale Einreichung',
      content: (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, bgcolor: colors.primary[400], mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                  Zusammenfassung
                </Typography>
                <Chip
                  label={
                    formData.status === 'completed'
                      ? 'Abgeschlossen'
                      : formData.status === 'in-progress'
                      ? 'In Bearbeitung'
                      : 'Offen'
                  }
                  sx={{
                    bgcolor: getStatusColor(formData.status),
                    color: '#fff',
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color={colors.grey[400]}>
                      Reklamationstyp
                    </Typography>
                    <Typography variant="body1">
                      {formData.processType === 'internal' ? 'Intern' : 'Extern'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color={colors.grey[400]}>
                      Priorität
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        formData.priority === 'high'
                          ? 'Hoch'
                          : formData.priority === 'medium'
                          ? 'Mittel'
                          : 'Niedrig'
                      }
                      sx={{
                        bgcolor: getPriorityColor(formData.priority),
                        color: '#fff',
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color={colors.grey[400]}>
                      Datum
                    </Typography>
                    <Typography variant="body1">
                      {formData.date
                        ? new Date(formData.date).toLocaleDateString('de-DE')
                        : 'Kein Datum ausgewählt'}
                    </Typography>
                  </Box>

                  {formData.deadline && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color={colors.grey[400]}>
                        Fälligkeitsdatum
                      </Typography>
                      <Typography variant="body1">
                        {new Date(formData.deadline).toLocaleDateString('de-DE')}
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color={colors.grey[400]}>
                      Zugewiesen an
                    </Typography>
                    <Typography variant="body1">
                      {formData.recipientName || 'Nicht zugewiesen'}
                    </Typography>
                  </Box>

                  {formData.anr && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color={colors.grey[400]}>
                        Projekt
                      </Typography>
                      <Typography variant="body1">
                        {formData.anr}{' '}
                        {formData.auftragsbezeichnung &&
                          `- ${formData.auftragsbezeichnung}`}
                      </Typography>
                    </Box>
                  )}

                  {formData.referenceOrderNumber && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color={colors.grey[400]}>
                        Referenz
                      </Typography>
                      <Typography variant="body1">
                        {formData.referenceOrderNumber}
                      </Typography>
                    </Box>
                  )}

                  {formData.assignedTeam && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color={colors.grey[400]}>
                        Team
                      </Typography>
                      <Typography variant="body1">{formData.assignedTeam}</Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color={colors.grey[400]}>
                    Beschreibung
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {formData.description || 'Keine Beschreibung vorhanden'}
                  </Typography>
                </Grid>

                {formData.resolutionDetails && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color={colors.grey[400]}>
                      Kurzfristige Maßnahmen
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {formData.resolutionDetails}
                    </Typography>
                  </Grid>
                )}
                
                {formData.improvementMeasures && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color={colors.grey[400]}>
                      Langfristige Verbesserungen
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {formData.improvementMeasures}
                    </Typography>
                  </Grid>
                )}

                {formData.processImprovements.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color={colors.grey[400]}>
                      Verknüpfte Prozessverbesserungen
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {formData.processImprovements.map((processId) => {
                        const process = processes.find((p) => p.id === processId);
                        return (
                          <Chip
                            key={processId}
                            label={process ? process.title : processId}
                            size="small"
                            color="primary"
                          />
                        );
                      })}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color={colors.grey[400]}>
                    Anhänge
                  </Typography>
                  {selectedFiles.length > 0 || uploadedFiles.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {selectedFiles.map((file, index) => (
                        <Chip
                          key={`new-${index}`}
                          label={file.name}
                          size="small"
                          icon={<AttachFileIcon />}
                          variant="outlined"
                        />
                      ))}
                      {uploadedFiles.map((file, index) => (
                        <Chip
                          key={`existing-${index}`}
                          label={file.originalName || `Datei ${index + 1}`}
                          size="small"
                          icon={<AttachFileIcon />}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color={colors.grey[500]}>
                      Keine Anhänge
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>

            <Typography variant="body2" color={colors.grey[400]} sx={{ mb: 3 }}>
              Bitte überprüfen Sie alle Informationen vor der Einreichung. Nach dem
              Absenden wird eine Aufgabe im Kanban-Board erstellt.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : isEditMode ? <SaveIcon /> : <CheckCircleIcon />}
                onClick={handleSubmit}
                disabled={submitting || isLocked}
                sx={{
                  px: 6,
                  py: 2,
                  bgcolor: isDarkMode ? colors.greenAccent[600] : colors.greenAccent[500],
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  borderRadius: '8px',
                  '&:hover': {
                    bgcolor: isDarkMode ? colors.greenAccent[500] : colors.greenAccent[400],
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
                className="shadow-lg hover:shadow-xl"
              >
                {submitting
                  ? 'Wird gespeichert...'
                  : isEditMode
                  ? 'Änderungen speichern'
                  : 'Reklamation einreichen'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 200px)',
        }}
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ m: '20px' }} className="max-w-7xl mx-auto">
      {/* HEADER */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={4}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: isDarkMode ? 'rgba(25, 30, 40, 0.8)' : 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
        className="border-l-4 border-blue-500 shadow-md"
      >
        <Box>
          <Typography 
            variant="h2" 
            fontWeight="bold" 
            sx={{ 
              color: isDarkMode ? colors.grey[100] : colors.primary[500],
              mb: 1
            }}
            className="text-3xl md:text-4xl"
          >
            {isEditMode
              ? 'Reklamation bearbeiten'
              : 'Neue Reklamation erstellen'}
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ color: colors.greenAccent[isDarkMode ? 400 : 600] }}
            className="text-sm md:text-base"
          >
            {isEditMode
              ? 'Bestehende Reklamation aktualisieren'
              : 'Reklamations- und Verbesserungsprozess starten'}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<ArrowBackIcon />}
            sx={{ 
              borderColor: isDarkMode ?  colors.grey[400] : colors.grey[300], 
              color: isDarkMode ?  colors.grey[200] : colors.grey[400],
              '&:hover': {
                borderColor: isDarkMode ? colors.grey[300] : colors.grey[400],
                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
              }
            }}
            className="hover:shadow-sm transition-all duration-200"
          >
            Abbrechen
          </Button>
        </Box>
      </Box>

      {/* STEPPER */}
      <Box 
        sx={{ 
          maxWidth: '100%', 
          mb: 4,
          bgcolor: isDarkMode ? 'rgba(25, 35, 50, 0.7)' : 'rgba(250, 250, 250, 0.9)',
          p: 2,
          borderRadius: 2,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
        className="border border-gray-200 dark:border-gray-700"
      >
        <Stepper 
          activeStep={activeStep} 
          orientation="vertical"
          sx={{
            '& .MuiStepLabel-root': {
              color: isDarkMode ? colors.grey[200] : colors.grey[800],
            },
            '& .MuiStepIcon-root': {
              color: colors.blueAccent[isDarkMode ? 500 : 600],
              '&.Mui-active': {
                color: colors.greenAccent[isDarkMode ? 500 : 600],
              },
              '&.Mui-completed': {
                color: colors.greenAccent[isDarkMode ? 500 : 600],
              },
            },
            '& .MuiStepConnector-line': {
              borderColor: isDarkMode ? colors.grey[700] : colors.grey[300],
            }
          }}
        >
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                optional={
                  <Typography 
                    variant="caption" 
                    sx={{ color: isDarkMode ? colors.grey[400] : colors.grey[600] }}
                    className="text-xs italic"
                  >
                    {step.description}
                  </Typography>
                }
              >
                <Typography 
                  variant="h6" 
                  sx={{ color: isDarkMode ? colors.grey[100] : colors.primary[500] }}
                  className="font-medium"
                >
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent sx={{ borderColor: isDarkMode ? colors.grey[700] : colors.grey[300] }}>
                <Box className="bg-opacity-50 rounded-md p-2">
                  {step.content}
                </Box>
                <Box sx={{ mb: 2, mt: 3 }} className="flex justify-between items-center flex-wrap">
                  <div className="flex gap-2">
                    <Button
                      variant="outlined"
                      disabled={index === 0}
                      onClick={handleBack}
                      startIcon={<ArrowBackIcon />}
                      sx={{ 
                        borderColor: isDarkMode ? colors.grey[400] : colors.grey[300],
                        color: isDarkMode ?  colors.grey[200] : colors.grey[400],
                        '&:hover': {
                          borderColor: isDarkMode ? colors.grey[300] : colors.grey[400],
                          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                        }
                      }}
                      className="transition-all duration-200"
                    >
                      Zurück
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        bgcolor: colors.blueAccent[isDarkMode ? 700 : 600],
                        color: '#fff',
                        '&:hover': {
                          bgcolor: colors.blueAccent[isDarkMode ? 600 : 500],
                        },
                      }}
                      className="shadow-sm hover:shadow transition-all duration-200"
                    >
                      {index === steps.length - 1
                        ? 'Überprüfen'
                        : 'Weiter'}
                    </Button>
                  </div>
                  <Typography 
                    variant="caption" 
                    sx={{ color: isDarkMode ? colors.grey[400] : colors.grey[600] }}
                    className="text-xs italic mt-2 md:mt-0"
                  >
                    Schritt {index + 1} von {steps.length}
                  </Typography>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* ANR Modal for Project Selection */}
      <ANRModal
        open={anrModalOpen}
        onClose={() => setAnrModalOpen(false)}
        onSelect={handleAnrSelect}
        isDarkMode={theme.palette.mode === 'dark'}
      />

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Änderungen verwerfen?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Es gibt ungespeicherte Änderungen. Sind Sie sicher, dass Sie die Seite
            verlassen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
            Abbrechen
          </Button>
          <Button
            onClick={() => navigate('/reklamation')}
            color="primary"
            autoFocus
          >
            Verwerfen und zurück
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Helper function to create alpha colors
function alpha(color, opacity) {
  // Simple function to handle hex colors
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  // For non-hex colors, just return with opacity
  return color;
}

export default ReklamationForm;